/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { DEFAULT_CONSENT_OPTIONS, useConsent } from "@packages/consent";
import { URL_COOKIE_POLICY } from "@packages/sdk";
import { HallowDataTestId } from "@tools/playwright/global";

import {
  numericPercentages,
  radius,
  spacing,
} from "../../../../global/emotion";
import { useDynamicViewport } from "../../../hooks/useDynamicViewport";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { Modal } from "../../_base/modals/Modal/Modal.emotion";
import { ModalHeader } from "../../_base/modals/ModalHeader/ModalHeader.emotion";

const buttonStyle: Interpolation = {
  justifyContent: "center",
  width: numericPercentages[100],
  [cssMediaQueryMinWidthTablet]: { width: "fit-content" },
};
const buttonsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
  width: numericPercentages[100],
  [cssMediaQueryMinWidthTablet]: { width: "auto" },
};
const modalContentStyle: Interpolation = {
  alignItems: "flex-start",
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  borderTopLeftRadius: radius.l,
  borderTopRightRadius: radius.l,
  bottom: 0,
  display: "flex",
  flexDirection: "column",
  gap: spacing.l,
  justifyContent: "space-between",
  maxWidth: 1000,
  padding: spacing.xl,
  top: "unset",
  [cssMediaQueryMinWidthTablet]: {
    bottom: 0,
    flexDirection: "row",
    gap: spacing.xl,
    padding: spacing.xl,
    top: "unset",
  },
};
const linkStyle: Interpolation = {
  textDecoration: "underline",
};
const textStyle: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.s,
  textAlign: "start",
};
const titleStyle: Interpolation = {
  justifyContent: "flex-start",
  margin: spacing.none,
  padding: spacing.none,
};

const titleTextStyle: Interpolation = {
  textAlign: "start",
  justifyContent: "flex-start",
  margin: spacing.none,
};

export const ConsentModal = () => {
  const isDisplayDesktop = useDynamicViewport({ minimumWidth: 768 });
  const t = useTranslations();

  const { isConsentModalVisible, saveConsent, setIsConsentModalVisible } =
    useConsent();

  const onClickAcceptAll = () => {
    saveConsent({ consent: DEFAULT_CONSENT_OPTIONS });
    setIsConsentModalVisible(false);
  };

  const onClickManage = () => {
    const newUrl = `${window.location.href.split("#")[0]}#cookie-manager`;
    window.location.href = newUrl;
    window?.history.pushState(null, "", newUrl);
  };

  return (
    <Modal
      contentCSS={modalContentStyle}
      onOpenChange={setIsConsentModalVisible}
      open={isConsentModalVisible}
    >
      <div css={textStyle}>
        <ModalHeader
          size="l"
          type="title"
          data-testid={HallowDataTestId.ConsentModalTitle}
          css={titleStyle}
          titleCSS={titleTextStyle}
        >
          {t("cookie_manager_title")}
        </ModalHeader>
        <Text size="m" type="body">
          {t("cookie_manager_popup_description_no_var")}{" "}
          <a
            href={URL_COOKIE_POLICY}
            rel="noreferrer"
            target="_blank"
            css={linkStyle}
          >
            {t("cookie_manager_policy_link_uppercase")}
          </a>
        </Text>
      </div>
      <div css={buttonsStyle}>
        <Button
          onClick={() => onClickAcceptAll()}
          size={isDisplayDesktop ? "l" : "m"}
          css={buttonStyle}
          type="button"
        >
          {t("button_accept")}
        </Button>
        <Button
          onClick={() => onClickManage()}
          size={isDisplayDesktop ? "l" : "m"}
          css={buttonStyle}
          type="button"
          variant="neutral"
        >
          {t("button_manage")}
        </Button>
      </div>
    </Modal>
  );
};

ConsentModal.displayName = "ConsentModal";
